import { useEffect, useRef } from "react";
import { BACKEND_URL } from "../lib/api";

/**
 * useRealtime - subscribes to /api/ws and dispatches events via handler callback.
 * Auto-reconnects with backoff. Sends ping every 25s.
 */
export function useRealtime(token, onMessage, onOpen) {
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;
  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  useEffect(() => {
    if (!token) return;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        openRef.current?.();
        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 25000);
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          handlerRef.current?.(data);
        } catch (err) {
          // ignore
        }
      };

      ws.onclose = () => {
        if (pingRef.current) clearInterval(pingRef.current);
        if (stopped) return;
        // reconnect with delay
        reconnectRef.current = setTimeout(connect, 2500);
      };
      ws.onerror = () => {
        try { ws.close(); } catch (e) {}
      };
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (pingRef.current) clearInterval(pingRef.current);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [token]);
}
